/* eslint-disable max-lines -- TODO: extract bulk-download wiring into a sub-component */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import {
  parseApiError,
  COST_RANGE_DIMENSIONS,
  type CostRangeDimensionId,
  type OfferVersion,
} from '@ooh/contracts';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useClientsList } from '../../../features/clients/api';
import { offerQueries, useOffer } from '../../../features/offers/api';
import { listVersionsOptions } from '../../../features/offers/api/queries';
import {
  deriveWorkspacePermissionFlags,
  deriveWorkspaceShellAccess,
} from '../../../features/offers/lib/workspace-access';
import type { SortDir, SortKey } from '../../../features/offers/units-tab/units-tab-toolbar';
import { UNITS_TABLE_SORT_KEYS } from '../../../features/offers/units-tab/units-tab-columns';
import type { OfferTableRevealSurface } from '../../../features/offers/units-tab/units-tab-page';
import {
  UNIT_DECISIONS_SORT_KEYS,
  pickDefaultVersionId,
  type UnitDecisionSortKey as DecisionSortKey,
} from '@ooh/screens';
import type { UnitsTabFilterValue } from '../../../features/offers/units-tab/units-tab-page';
import { PageTitleContext, type RouterContext } from '../../__root';
import {
  OFFER_WORKSPACE_TABS,
  OFFER_WORKSPACE_RETIRED_TAB_VALUES,
  type OfferWorkspaceTab,
  WorkspaceErrorState,
  WorkspacePendingState,
  WorkspaceShell,
} from '../../../features/offers/workspace/workspace-shell';
import { useHasPermission } from '../../../hooks/use-has-permission';
import { useHasWildcardPermission } from '../../../hooks/use-has-wildcard-permission';
import { FEATURE_FLAG_PUBLIC_OFFER_LINKS } from '@ooh/contracts';
import { useBulkDownloadSpecs } from '../../../features/media-units/hooks/use-bulk-download-specs';
import {
  readRangeFilterValues,
  buildNextRangeFilterValues,
} from '../../../hooks/use-range-filter-params';

/**
 * Coerces a URL query-string numeric range bound (e.g. "10") to a number; absent
 * stays undefined. `.catch(undefined)` degrades a malformed value (e.g. "abc" ->
 * NaN) to undefined instead of throwing, matching every sibling field's
 * `.default().catch()` graceful-degradation behavior.
 */
const rangeNumberParam = z.coerce.number().optional().catch(undefined);

/**
 * Zod shape for the `<id>_min`/`<id>_max` scalar range params of every cost
 * dimension in COST_RANGE_DIMENSIONS — one pair of `rangeNumberParam` entries
 * per dimension, generated instead of hand-listed (mirrors the identical
 * generation in `OfferUnitFiltersSchema`, @ooh/contracts/schemas/offer-unit.ts).
 */
type CostRangeSearchShape = {
  [K in CostRangeDimensionId as `${K}_min`]: typeof rangeNumberParam;
} & {
  [K in CostRangeDimensionId as `${K}_max`]: typeof rangeNumberParam;
};

const costRangeSearchShape: CostRangeSearchShape = Object.fromEntries(
  COST_RANGE_DIMENSIONS.flatMap((d) => [
    [`${d.id}_min`, rangeNumberParam],
    [`${d.id}_max`, rangeNumberParam],
  ]),
) as CostRangeSearchShape;

/**
 * Exported for direct unit testing of the retired-`group`-param tolerance
 * (see `__tests__/offer-workspace-search-schema.test.ts`) — do not import this
 * to read search state; use `Route.useSearch()` for that.
 */
export const OfferWorkspaceSearchSchema = z
  .object({
    // Retired `pricing`/`availability` values still parse (rather than
    // `.catch()`ing straight to `units`) so the route component below can
    // redirect them to `?tab=units` with replace semantics and reveal the
    // columns they were saved for, instead of the value being silently lost
    // before the redirect ever sees it (offer-unified-table, CU-86cbc3r8b).
    tab: z
      .enum([...OFFER_WORKSPACE_TABS, ...OFFER_WORKSPACE_RETIRED_TAB_VALUES])
      .default('units')
      .catch('units'),
    unitsSortBy: z.enum(UNITS_TABLE_SORT_KEYS).default('sortOrder').catch('sortOrder'),
    unitsSortDir: z.enum(['asc', 'desc']).default('asc').catch('asc'),
    decisionsSortBy: z.enum(UNIT_DECISIONS_SORT_KEYS).default('decision').catch('decision'),
    decisionsSortDir: z.enum(['asc', 'desc']).default('asc').catch('asc'),
    // Retired single-key sort parameter. Accepted but ignored for stale bookmarks.
    sort: z.unknown().optional(),
    region: z.array(z.string()).default([]).catch([]),
    county: z.array(z.string()).default([]).catch([]),
    agglomeration: z.array(z.string()).default([]).catch([]),
    city: z.array(z.string()).default([]).catch([]),
    format: z.array(z.string()).default([]).catch([]),
    mediaType: z.array(z.string()).default([]).catch([]),
    supplier: z.array(z.string()).default([]).catch([]),
    lighting: z.array(z.string()).default([]).catch([]),
    supplierNumber: z.array(z.string()).default([]).catch([]),
    grwId: z.array(z.string()).default([]).catch([]),
    /** Free-text multi-column search term (catalog parity). */
    search: z.string().default('').catch(''),
    // ─── Catalog-parity filters (CU-86ca9k73a) ───
    material: z.array(z.string()).default([]).catch([]),
    multiformat: z.array(z.string()).default([]).catch([]),
    tags: z.array(z.string()).default([]).catch([]),
    /** Dedicated address text filter (distinct from the multi-column `search` above). */
    address: z.string().default('').catch(''),
    // ─── Column-parity filters (catalog-column-filter-parity) ───
    notes: z.array(z.string()).default([]).catch([]),
    roadNumber: z.array(z.string()).default([]).catch([]),
    area_min: rangeNumberParam,
    area_max: rangeNumberParam,
    width_min: rangeNumberParam,
    width_max: rangeNumberParam,
    height_min: rangeNumberParam,
    height_max: rangeNumberParam,
    audience_min: rangeNumberParam,
    audience_max: rangeNumberParam,
    ...costRangeSearchShape,
    // Retired pre-4.5 visual-grouping search param. Accepted-but-ignored (never
    // read) so a stale bookmark/history entry containing `?group=city` degrades
    // gracefully instead of failing validateSearch with a ZodError. `.strict()`
    // is otherwise kept so unrelated unknown keys still fail loudly instead of
    // being silently swallowed.
    group: z.unknown().optional(),
  })
  .strict();

/**
 * Resolves the retired `?tab=pricing`/`?tab=availability` addresses to a
 * redirect target and the tab actually rendered — extracted out of
 * `OfferWorkspaceRoute` purely to stay within the file's `complexity`
 * budget. `activeTab` never carries the retired value forward to
 * `WorkspaceShell`, whose `TabsList` only ever renders the 3 real tabs.
 */
function resolveWorkspaceTab(tab: OfferWorkspaceTab | 'pricing' | 'availability'): {
  retiredTab: (typeof OFFER_WORKSPACE_RETIRED_TAB_VALUES)[number] | undefined;
  activeTab: OfferWorkspaceTab;
} {
  const isRetired = tab === 'pricing' || tab === 'availability';
  return {
    retiredTab: isRetired ? tab : undefined,
    activeTab: isRetired ? 'units' : tab,
  };
}

/**
 * Resolves the retired-tab redirect target and the one-visit column reveal
 * for the merged offer table (offer-unified-table: "A retired address
 * reveals the columns it was saved for"). `revealSurface` is captured ONCE,
 * via a lazy `useState` initializer, from whichever `tab` value the URL had
 * when this hook first mounted — independent of `retiredTab`/`activeTab`
 * below, which stay reactively derived from the current `tab` on every
 * render. The caller's own effect replaces `?tab=pricing`/`?tab=availability`
 * with `?tab=units` on mount; deriving `revealSurface` from that same
 * reactive `tab` would recompute it as `undefined` the instant that redirect
 * lands, clobbering the reveal before the table ever gets to show it.
 */
export function useRetiredTabReveal(tab: OfferWorkspaceTab | 'pricing' | 'availability') {
  const [revealSurface] = React.useState<OfferTableRevealSurface | undefined>(
    () => resolveWorkspaceTab(tab).retiredTab,
  );
  const { retiredTab, activeTab } = resolveWorkspaceTab(tab);
  return { retiredTab, activeTab, revealSurface };
}

function createLoaderData(isForbidden: boolean, userId: number | null) {
  return { isForbidden, userId } as const;
}

/** Extracted so `OfferWorkspaceRoute`'s own complexity doesn't carry the two
 * `??` fallbacks for a version that may not exist. */
function resolveActiveAcceptedVersionIds(version: OfferVersion | null): {
  id: number | null;
  number: number | null;
} {
  return { id: version?.id ?? null, number: version?.versionNumber ?? null };
}

async function isWorkspaceForbiddenByLoader(context: RouterContext, offerId: string) {
  try {
    await context.queryClient.ensureQueryData(offerQueries.detail(context.auth, offerId));
    return false;
  } catch (error) {
    if (parseApiError(error).statusCode === 403) {
      return true;
    }

    throw error;
  }
}

export const Route = createFileRoute('/offers/$offerId/')({
  validateSearch: OfferWorkspaceSearchSchema,
  loader: async ({ context, params }) => {
    const userId = context.auth.user?.id ?? null;
    // Do not early-bail based on role permissions here — a user may have access
    // to a specific offer via a share even without the offer.view role permission.
    // Let the API (via ensureQueryData) be the authoritative access check.
    return createLoaderData(await isWorkspaceForbiddenByLoader(context, params.offerId), userId);
  },
  component: OfferWorkspaceRoute,
});

interface OfferWorkspaceRouteContentProps {
  canApproveAny: boolean;
  canManageAny: boolean;
  canSetStatusAny: boolean;
  /** Whether `auth.permissions` has finished resolving (not `null`) — threaded to
   * the finalization panel so it never asserts a denial while access is still
   * fail-closed-by-default (offer-approval / "Approval Action UI in Offer
   * Workspace"). */
  isAccessResolved: boolean;
  /** `reservation.manage` — threaded to the preliminary-reservation action. */
  canManageReservation: boolean;
  /** The offer's currently active `ACCEPTED` version id/number, or `null`
   * when none is accepted — threaded to the preliminary-reservation action. */
  activeAcceptedVersionId: number | null;
  activeAcceptedVersionNumber: number | null;
  filterValue: UnitsTabFilterValue;
  offerId: string;
  onFilterChange: (filterValue: UnitsTabFilterValue) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /** Dedicated address text filter (catalog parity, CU-86ca9k73a). */
  address: string;
  onAddressChange: (address: string) => void;
  /** Numeric range dimensions (catalog parity, CU-86ca9k73a). */
  rangeValue: Record<string, string[]>;
  onRangeChange: (rangeValue: Record<string, string[]>) => void;
  onSortChange: (sortKey: SortKey, sortDir: SortDir) => void;
  userId: number | null;
  sortKey: SortKey;
  sortDir: SortDir;
  decisionVersionId?: number;
  decisionsSortKey: DecisionSortKey;
  decisionsSortDir: SortDir;
  onDecisionsSortChange: (sortKey: DecisionSortKey, sortDir: SortDir) => void;
  tab: OfferWorkspaceTab;
  onTabChange: (tab: OfferWorkspaceTab) => void;
  /** Retired-address one-visit column reveal (offer-unified-table). */
  revealSurface?: OfferTableRevealSurface;
  /** Resolved at route level (media_unit.view permission) */
  canDownloadSpecs: boolean;
  /** Resolved at route level (cost.view permission, CU-86ca9k73a) */
  canViewCosts: boolean;
  /** Resolved at route level (wildcard cost.view_rent grant, CU-86cbgnt3z) */
  canViewRent: boolean;
  /** Resolved at route level (supplier.view permission, catalog-column-filter-parity) */
  canViewSupplierDetails: boolean;
  onBulkDownloadSpecs?: (ids: number[]) => void;
  bulkDownloadPendingManifest?: import('@ooh/contracts').BulkDownloadManifestResponse | null;
  confirmBulkDownload?: () => Promise<void>;
  cancelBulkDownload?: () => void;
}

function isWorkspaceLoading(offerQuery: ReturnType<typeof useOffer>) {
  return offerQuery.isLoading;
}

function hasWorkspaceError(offerQuery: ReturnType<typeof useOffer>) {
  return offerQuery.isError || !offerQuery.data;
}

function isWorkspaceForbiddenQuery(offerQuery: ReturnType<typeof useOffer>) {
  return offerQuery.isError && parseApiError(offerQuery.error).statusCode === 403;
}

function OfferWorkspaceRouteContent({
  canApproveAny,
  canManageAny,
  canSetStatusAny,
  isAccessResolved,
  canManageReservation,
  activeAcceptedVersionId,
  activeAcceptedVersionNumber,
  filterValue,
  onFilterChange,
  search,
  onSearchChange,
  address,
  onAddressChange,
  rangeValue,
  onRangeChange,
  offerId,
  onSortChange,
  sortKey,
  sortDir,
  decisionVersionId,
  decisionsSortKey,
  decisionsSortDir,
  onDecisionsSortChange,
  onTabChange,
  tab,
  revealSurface,
  userId,
  canDownloadSpecs,
  canViewCosts,
  canViewRent,
  canViewSupplierDetails,
  onBulkDownloadSpecs,
  bulkDownloadPendingManifest,
  confirmBulkDownload,
  cancelBulkDownload,
}: OfferWorkspaceRouteContentProps) {
  const clientsQuery = useClientsList();
  const offerQuery = useOffer(offerId);
  const hasPublicOfferLinksFlag = useHasPermission(FEATURE_FLAG_PUBLIC_OFFER_LINKS);

  if (isWorkspaceLoading(offerQuery)) {
    return <WorkspacePendingState />;
  }

  if (hasWorkspaceError(offerQuery)) {
    if (isWorkspaceForbiddenQuery(offerQuery)) {
      return <WorkspaceShell isForbidden />;
    }

    return <WorkspaceErrorState />;
  }

  const offer = offerQuery.data;
  if (!offer) {
    return <WorkspaceErrorState />;
  }

  const client = clientsQuery.data?.find((item) => item.id === offer.clientId);
  const access = deriveWorkspaceShellAccess({
    canApproveAny,
    canManageAny,
    canSetStatusAny,
    offer,
    userId,
  });
  const canManagePublicLink = hasPublicOfferLinksFlag && access.canEdit;

  return (
    <WorkspaceShell
      activeTab={tab}
      canAdminChangeStatus={access.canAdminChangeStatus}
      canConfirmCampaign={access.canConfirmCampaign}
      canRecordSale={access.canRecordSale}
      canEdit={access.canEdit}
      hasUnsecuredUnits={access.hasUnsecuredUnits}
      isAccessResolved={isAccessResolved}
      canManageReservation={canManageReservation}
      activeAcceptedVersionId={activeAcceptedVersionId}
      activeAcceptedVersionNumber={activeAcceptedVersionNumber}
      canManagePublicLink={canManagePublicLink}
      canManageSharing={access.canManageSharing}
      canTrash={access.canTrash}
      client={client}
      offer={offer}
      onUnitsFilterChange={onFilterChange}
      onUnitsSearchChange={onSearchChange}
      onUnitsSortChange={onSortChange}
      unitsFilterValue={filterValue}
      unitsSearchValue={search}
      unitsAddressValue={address}
      onUnitsAddressChange={onAddressChange}
      unitsRangeValue={rangeValue}
      onUnitsRangeChange={onRangeChange}
      canViewCosts={canViewCosts}
      canViewRent={canViewRent}
      canViewSupplierDetails={canViewSupplierDetails}
      onTabChange={onTabChange}
      unitsSortKey={sortKey}
      unitsSortDir={sortDir}
      unitsDecisionVersionId={decisionVersionId}
      revealSurface={revealSurface}
      decisionsSortKey={decisionsSortKey}
      decisionsSortDir={decisionsSortDir}
      onDecisionsSortChange={onDecisionsSortChange}
      onBulkDownloadSpecs={canDownloadSpecs ? onBulkDownloadSpecs : undefined}
      bulkDownloadPendingManifest={bulkDownloadPendingManifest}
      confirmBulkDownload={confirmBulkDownload}
      cancelBulkDownload={cancelBulkDownload}
    />
  );
}

export function OfferWorkspaceRoute() {
  const { offerId } = Route.useParams();
  const {
    region,
    county,
    agglomeration,
    city,
    format,
    mediaType,
    grwId,
    lighting,
    search,
    unitsSortBy = 'sortOrder',
    unitsSortDir = 'asc',
    decisionsSortBy = 'decision',
    decisionsSortDir = 'asc',
    supplier,
    supplierNumber,
    tab,
    material,
    multiformat,
    tags,
    address,
    notes,
    roadNumber,
  } = Route.useSearch();
  // Full search snapshot, used only to derive the numeric-range dimensions
  // below (readRangeFilterValues reads whichever `<id>_min`/`<id>_max` keys
  // are present — cheaper than destructuring all 36 range fields by name).
  const searchParams = Route.useSearch();
  const loaderData = Route.useLoaderData();
  const navigate = useNavigate({ from: Route.fullPath });
  // Numeric range dimensions (CU-86ca9k73a) — reuses the neutral
  // `readRangeFilterValues`/`buildNextRangeFilterValues` PURE helpers from
  // `src/hooks/use-range-filter-params.ts` (catalog-parity source of truth),
  // but sourced from THIS route's typed `Route.useSearch()`/`navigate`
  // instead of calling the hook's own raw `useSearch()`/`useNavigate()` —
  // the hook's generic (non-route-scoped) `useSearch({ strict: false })` call
  // requires a live `RouterProvider`, which route-level unit tests
  // (`route-access.spec.tsx`) don't provide; `Route.useSearch()` is already
  // mocked there. `UnitsTabPage` receives the resolved value/setter as props
  // for the same reason (`units-tab-page.spec.tsx` etc.).
  const rangeValue = React.useMemo(
    () => readRangeFilterValues(searchParams as unknown as Record<string, unknown>),
    [searchParams],
  );
  const handleRangeChange = React.useCallback(
    (nextRangeValue: Record<string, string[]>) => {
      void (
        navigate as unknown as (opts: {
          replace: boolean;
          search: (prev: Record<string, unknown>) => Record<string, unknown>;
        }) => void
      )({
        replace: true,
        search: (prev) => buildNextRangeFilterValues(prev, nextRangeValue),
      });
    },
    [navigate],
  );
  // Retired `?tab=pricing`/`?tab=availability` addresses redirect to `units`
  // with replace semantics, revealing the columns they were saved for on
  // this visit only (offer-unified-table: "A retired address reveals the
  // columns it was saved for").
  const { retiredTab, activeTab, revealSurface } = useRetiredTabReveal(tab);
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('offers');
  // Use fail-closed for canManageAny: unknown/loading permissions → false.
  // This ensures a view-only share grantee sees a read-only workspace even when
  // the permissions fetch is still in flight (rather than temporarily showing edit controls).
  // The server is the authoritative enforcement point for actual mutations.
  const { auth } = useRouteContext({ from: '__root__' });
  const versionsQuery = useQuery(listVersionsOptions(auth, offerId));
  const decisionVersionId = React.useMemo(
    () => pickDefaultVersionId(versionsQuery.data?.versions ?? []) ?? undefined,
    [versionsQuery.data?.versions],
  );
  // The offer's currently active ACCEPTED version — threaded to the
  // preliminary-reservation action (offer-accept-reservation). Distinct from
  // decisionVersionId above, which falls back to the most-recent client-
  // acted or latest version when nothing is ACCEPTED.
  const activeAcceptedVersion = React.useMemo(
    () => versionsQuery.data?.versions.find((version) => version.status === 'ACCEPTED') ?? null,
    [versionsQuery.data?.versions],
  );
  const activeAcceptedVersionIds = resolveActiveAcceptedVersionIds(activeAcceptedVersion);
  const { canApproveAny, canManageAny, canSetStatusAny } = deriveWorkspacePermissionFlags(
    auth.permissions,
  );
  // Resolved here (the feature-level parent owning the whole offer-workspace
  // screen context) — see apps/web/AGENTS.md's permission-hook placement rule.
  const canManageReservation = useHasPermission('reservation.manage');
  // auth.permissions is null only until the permissions fetch resolves (see the
  // fail-closed comment above) — the finalization panel reads this so it never
  // asserts a denial while access is still unresolved.
  const isAccessResolved = auth.permissions !== null;
  // Bulk download permission — same permission as single-attachment download (decision D-8)
  const canDownloadSpecs = useHasPermission('media_unit.view');
  // Gates internal cost-range filter dimensions on the units-tab filter panel
  // (catalog parity, CU-86ca9k73a) — resolved here, the feature-level parent
  // that owns the full offer-workspace screen context (WEB_ARCHITECTURE.md's
  // "resolve permission booleans in feature-level parents" rule).
  const canViewCosts = useHasPermission('cost.view');
  // Omits the rent range filter specifically, on top of canViewCosts — rent's
  // own wildcard cost.view_rent grant is narrower (field-visibility,
  // CU-86cbgnt3z).
  const canViewRent = useHasWildcardPermission('cost.view_rent');
  // Gates the three supplier-contact filter fields on the units-tab filter
  // panel (catalog-column-filter-parity) — resolved here, same reasoning as
  // canViewCosts above.
  const canViewSupplierDetails = useHasPermission('supplier.view');
  const {
    start: startBulkDownload,
    pendingManifest: bulkDownloadPendingManifest,
    confirmDownload,
    cancelDownload,
  } = useBulkDownloadSpecs();

  React.useEffect(() => {
    setPageTitle(t('workspace.pageTitle'));
  }, [setPageTitle, t]);

  React.useEffect(() => {
    if (!retiredTab) return;
    void navigate({ replace: true, search: (prev) => ({ ...prev, tab: 'units' }) });
  }, [retiredTab, navigate]);

  if (loaderData.isForbidden) {
    return <WorkspaceShell isForbidden />;
  }

  function handleTabChange(nextTab: OfferWorkspaceTab) {
    void navigate({ replace: true, search: (prev) => ({ ...prev, tab: nextTab }) });
  }

  function handleSortChange(nextSort: SortKey, nextDir: SortDir) {
    void navigate({
      replace: true,
      search: (prev) => ({ ...prev, unitsSortBy: nextSort, unitsSortDir: nextDir }),
    });
  }

  function handleDecisionsSortChange(nextSort: DecisionSortKey, nextDir: SortDir) {
    void navigate({
      replace: true,
      search: (prev) => ({ ...prev, decisionsSortBy: nextSort, decisionsSortDir: nextDir }),
    });
  }

  function handleFilterChange(nextFilterValue: UnitsTabFilterValue) {
    void navigate({
      replace: true,
      search: (prev) => ({
        ...prev,
        region: nextFilterValue.region,
        county: nextFilterValue.county,
        agglomeration: nextFilterValue.agglomeration,
        city: nextFilterValue.city,
        format: nextFilterValue.format,
        mediaType: nextFilterValue.mediaType,
        supplier: nextFilterValue.supplier,
        lighting: nextFilterValue.lighting,
        supplierNumber: nextFilterValue.supplierNumber,
        grwId: nextFilterValue.grwId,
        material: nextFilterValue.material,
        multiformat: nextFilterValue.multiformat,
        tags: nextFilterValue.tags,
        notes: nextFilterValue.notes,
        roadNumber: nextFilterValue.roadNumber,
      }),
    });
  }

  function handleSearchChange(nextSearch: string) {
    void navigate({ replace: true, search: (prev) => ({ ...prev, search: nextSearch }) });
  }

  function handleAddressChange(nextAddress: string) {
    void navigate({ replace: true, search: (prev) => ({ ...prev, address: nextAddress }) });
  }

  return (
    <OfferWorkspaceRouteContent
      canApproveAny={canApproveAny}
      canManageAny={canManageAny}
      canSetStatusAny={canSetStatusAny}
      isAccessResolved={isAccessResolved}
      canManageReservation={canManageReservation}
      activeAcceptedVersionId={activeAcceptedVersionIds.id}
      activeAcceptedVersionNumber={activeAcceptedVersionIds.number}
      filterValue={{
        region,
        county,
        agglomeration,
        city,
        format,
        mediaType,
        supplier,
        lighting,
        supplierNumber,
        grwId,
        material,
        multiformat,
        tags,
        notes,
        roadNumber,
      }}
      onFilterChange={handleFilterChange}
      search={search}
      onSearchChange={handleSearchChange}
      address={address}
      onAddressChange={handleAddressChange}
      rangeValue={rangeValue}
      onRangeChange={handleRangeChange}
      offerId={offerId}
      onSortChange={handleSortChange}
      tab={activeTab}
      revealSurface={revealSurface}
      sortKey={unitsSortBy}
      sortDir={unitsSortDir}
      decisionVersionId={decisionVersionId}
      decisionsSortKey={decisionsSortBy}
      decisionsSortDir={decisionsSortDir}
      onDecisionsSortChange={handleDecisionsSortChange}
      userId={loaderData.userId}
      onTabChange={handleTabChange}
      canDownloadSpecs={canDownloadSpecs}
      canViewCosts={canViewCosts}
      canViewRent={canViewRent}
      canViewSupplierDetails={canViewSupplierDetails}
      onBulkDownloadSpecs={(ids) => void startBulkDownload(ids)}
      bulkDownloadPendingManifest={bulkDownloadPendingManifest}
      confirmBulkDownload={confirmDownload}
      cancelBulkDownload={cancelDownload}
    />
  );
}
